import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import type { ColumnaReporte, FilaReporte, TipoColumna } from '@/services/api/reportes.api';

interface ReportPreviewTablaProps {
  columnas: ColumnaReporte[];
  filas: FilaReporte[];
  cargando: boolean;
}

const formatoFecha = new Intl.DateTimeFormat('es-CO', { dateStyle: 'medium' });
const formatoFechaHora = new Intl.DateTimeFormat('es-CO', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

/**
 * Formatea igual que el Excel para que la vista previa no engañe: lo que se ve
 * acá es lo que va a bajar en el archivo.
 */
function formatear(valor: unknown, tipo: TipoColumna): string {
  if (valor === null || valor === undefined) return '—';

  switch (tipo) {
    case 'booleano':
      return valor ? 'Sí' : 'No';

    case 'numero': {
      const numero = Number(valor);
      return Number.isFinite(numero) ? numero.toLocaleString('es-CO') : '—';
    }

    case 'fecha':
    case 'fecha_hora': {
      const fecha = new Date(String(valor));
      if (Number.isNaN(fecha.getTime())) return '—';
      return tipo === 'fecha' ? formatoFecha.format(fecha) : formatoFechaHora.format(fecha);
    }

    default:
      return String(valor);
  }
}

export function ReportPreviewTabla({ columnas, filas, cargando }: ReportPreviewTablaProps) {
  if (cargando) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (columnas.length === 0) {
    return (
      <p className="p-8 text-center text-sm text-muted-foreground">
        Selecciona al menos una columna para ver el reporte.
      </p>
    );
  }

  if (filas.length === 0) {
    return (
      <p className="p-8 text-center text-sm text-muted-foreground">
        Ningún registro coincide con los filtros aplicados.
      </p>
    );
  }

  return (
    // El scroll horizontal vive acá dentro: un reporte de 20 columnas no debe
    // ensanchar el diálogo.
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {columnas.map((c) => (
              <TableHead key={c.clave} className="whitespace-nowrap">{c.titulo}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {filas.map((fila, indice) => (
            <TableRow key={indice}>
              {columnas.map((c) => (
                <TableCell key={c.clave} className="whitespace-nowrap text-xs">
                  {formatear(fila[c.clave], c.tipo)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
